import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { FieldType } from '../../types';
import { displayDate } from './MiscFunctions';
import { getBusinessLocale, globalValue } from '../apps/AppContext';
import dayjs from 'dayjs';

export interface ExportColumn<T> {
  Header: string;
  accessor: keyof T | ((row: T) => any);
  className?: string;
  fieldType?: FieldType;
}

// Shared header block used by every PDF report export: business logo (if cached),
// business name, report title, optional selected-period label, and generated timestamp.
// Returns the Y position where table content can safely start.
export const drawReportPdfHeader = (doc: jsPDF, title: string, periodLabel?: string): number => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const bizDetails = globalValue?.bizMaster?.bizDetails;
  const logoDataUrl: string | undefined = globalValue?.bizLogoDataUrl;
  let cursorY = 14;

  if (logoDataUrl) {
    try {
      // Use jsPDF's own decoded pixel dimensions — more reliable than the browser
      // Image element's naturalWidth/Height, which can misreport for some sources.
      const imgProps = doc.getImageProperties(logoDataUrl);
      const aspect = imgProps.height / imgProps.width;
      const maxWidth = 22;
      const maxHeight = 16;
      // Fit within the maxWidth x maxHeight box while preserving aspect ratio in both dimensions.
      let logoWidth = maxWidth;
      let logoHeight = logoWidth * aspect;
      if (logoHeight > maxHeight) {
        logoHeight = maxHeight;
        logoWidth = logoHeight / aspect;
      }
      doc.addImage(logoDataUrl, imgProps.fileType, 8, 8, logoWidth, logoHeight);
    } catch {
      // Unsupported/undecodable image — skip silently, header text still renders.
    }
  }

  if (bizDetails?.bizName) {
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(bizDetails.bizName, pageWidth / 2, cursorY, { align: 'center' });
    cursorY += 6;
  }

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(title, pageWidth / 2, cursorY, { align: 'center' });
  cursorY += 5;

  if (periodLabel) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(periodLabel, pageWidth / 2, cursorY, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    cursorY += 5;
  }

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text(`Generated: ${dayjs().format('DD-MMM-YYYY hh:mm A')}`, pageWidth / 2, cursorY, { align: 'center' });
  doc.setTextColor(0, 0, 0);
  cursorY += 4;

  return cursorY;
};

const exportTableToPDF = <T extends object>(
  columns: ExportColumn<T>[],
  data: T[],
  fileName: string = 'table.pdf',
  title: string = 'Table Data',
  totals?: Record<string, number>,
  periodLabel?: string,
) => {
  const orientation = columns.length > 5 ? 'landscape' : 'portrait';
  const doc = new jsPDF({ orientation });
  const pageWidth = doc.internal.pageSize.getWidth();

  const resolveAccessor = (obj: any, accessor: string | ((row: any) => any)) => {
    if (typeof accessor === 'string') {
      return accessor.split('.').reduce((acc, part) => acc && acc[part], obj);
    } else if (typeof accessor === 'function') {
      return accessor(obj);
    }
    return null;
  };

  // ── Header block ──────────────────────────────────────────
  const cursorY = drawReportPdfHeader(doc, title, periodLabel);

  // ── Table columns and rows ────────────────────────────────
  const tableColumn = columns.map(col => col.Header);
  const tableRows = data.map(row =>
    columns.map(col => {
      const value = resolveAccessor(row, col.accessor as string);
      if (value instanceof Date || col.fieldType === FieldType.date) {
        const dateValue = new Date(value);
        return isNaN(dateValue.getTime()) ? 'N/A' : displayDate(dateValue);
      }
      if (typeof value === 'number') {
        return new Intl.NumberFormat(getBusinessLocale(), {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);
      }
      return value !== null && value !== undefined ? String(value) : '';
    })
  );

  // ── Column styles ─────────────────────────────────────────
  const columnStyles = columns.reduce((acc: any, col: ExportColumn<T>, index: number) => {
    acc[index] = {};
    if (col.className?.includes('text-end')) {
      acc[index].halign = 'right';
    }
    if (col.className?.includes('bold')) {
      acc[index].fontStyle = 'bold';
    }
    return acc;
  }, {});

  // Scale font size for wide tables
  const fontSize = columns.length > 10 ? 7 : columns.length > 7 ? 8 : 10;

  // ── Totals footer row ─────────────────────────────────────
  const footRow: string[][] = totals && Object.keys(totals).length > 0
    ? [columns.map((col, i) => {
        const accessor = typeof col.accessor === 'string' ? col.accessor : '';
        const hasTotal = accessor !== '' && accessor in totals;
        if (hasTotal) {
          return new Intl.NumberFormat(getBusinessLocale(), {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(totals[accessor]);
        }
        return i === 0 ? 'Total' : '';
      })]
    : [];

  // ── Table ─────────────────────────────────────────────────
  autoTable(doc, {
    theme: 'grid',
    head: [tableColumn],
    body: tableRows,
    ...(footRow.length > 0 ? {
      foot: footRow,
      showFoot: 'lastPage',
      footStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: fontSize,
      },
    } : {}),
    headStyles: {
      fillColor: [46, 64, 87],   // Dark navy #2E4057
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: fontSize,
    },
    styles: { fontSize: fontSize },
    startY: cursorY + 2,
    columnStyles,
    didParseCell: (hookData: any) => {
      // Right-align header/footer cells that match text-end columns
      if (hookData.cell.section === 'head' || hookData.cell.section === 'foot') {
        const colDef = columns[hookData.column.index];
        if (colDef?.className?.includes('text-end')) {
          hookData.cell.styles.halign = 'right';
        }
      }
      if (hookData.cell.section === 'body') {
        // Bold bucket/highlight rows (e.g. aging detail bucket labels)
        const originalRow = data[hookData.row.index] as any;
        if (originalRow?.isHighlight) {
          hookData.cell.styles.fontStyle = 'bold';
        }
        // Red text for negative numbers
        const rawVal = hookData.cell.raw;
        if (typeof rawVal === 'string') {
          const num = parseFloat(rawVal.replace(/[^0-9.\-]/g, ''));
          if (!isNaN(num) && num < 0) {
            hookData.cell.styles.textColor = [192, 0, 0];
          }
        }
      }
    },
  });

  // Write page footers after autoTable finishes so total page count is final
  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 8,
      { align: 'center' }
    );
    doc.setTextColor(0, 0, 0);
  }

  doc.save(fileName);
};



export default exportTableToPDF;
